import { Popover as AntdPopover } from 'antd';
import type { ReactNode } from 'react';

// Import components
import PopoverContent from './PopoverContent';
import PopoverToolbox from './toolbox/PopoverToolbox';

// Import styles
import { PopoverWrapper, Container, PlaceholderContainer, PlaceholderText, Preview, Image } from './styles';

// Import utilities
import { ImageOnLoadRPC } from 'components/utilities';

// Import hooks
import { useAppSelector } from 'store/hooks';

// Import types
import type { Program } from 'types/views/epg';
import type { LinkDetails } from 'types/libraries/router';

type Props = {
	isDisabled: boolean;
	linkDetails: LinkDetails;
	program: Program;
	isContentBlocked: boolean;
	placeholderRatingText: string;
	image: string | undefined;
	children: ReactNode;
	dragged?: boolean;
	isUpcoming?: boolean;
	isNotEnded?: boolean;
	isCatchup?: boolean;
	isMobile?: boolean;
};

const ProgramPopover = ({
	isDisabled,
	linkDetails,
	program,
	isContentBlocked,
	placeholderRatingText,
	image,
	children,
	dragged = false,
	isUpcoming = false,
	isNotEnded = false,
	isCatchup = false,
	isMobile = false
}: Props) => {
	const reminders = useAppSelector(({ reminders }) => reminders.data);
	// If there is not any program, return null
	if (!program) return null;

	if (isDisabled) return children;

	// gets reminder object for its program
	const reminder = reminders.find(({ product_uuid }) => product_uuid === program.uuid);

	const reminderId = reminder?.id ?? false;

	const content = (
		<PopoverWrapper dragged={dragged}>
			<Container isMobile={isMobile}>
				{isContentBlocked ? (
					<PlaceholderContainer>
						<PlaceholderText>{placeholderRatingText}</PlaceholderText>
					</PlaceholderContainer>
				) : (
					<PopoverContent linkDetails={linkDetails} isCatchup={isCatchup} program={program} />
				)}
				{program.uuid && (
					<PopoverToolbox
						reminderId={reminderId}
						linkDetails={linkDetails}
						programId={program.uuid}
						isUpcoming={isUpcoming}
						isCatchup={isCatchup}
						isNotEnded={isNotEnded}
						startDate={program.since}
						channelId={program.channel_uuid}
					/>
				)}
			</Container>
			{!isMobile && (
				<ImageOnLoadRPC>
					{({ setError, setLoaded, isLoaded }) => (
						<Preview>
							<Image isLoaded={isLoaded} onError={setError} src={image} onLoad={setLoaded} alt="Preview" />
						</Preview>
					)}
				</ImageOnLoadRPC>
			)}
		</PopoverWrapper>
	);

	return (
		<AntdPopover content={content} placement="bottomLeft" trigger="hover" overlayClassName="epg-popover">
			{children}
		</AntdPopover>
	);
};

export default ProgramPopover;
